import { memo } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { TicketDetailData } from '@/entities/ticket';
import { cn } from '@/shared/lib/cn';
import { SatisfactionCallout, SatisfactionLine } from './SatisfactionCallout';
import styles from './TicketDetail.module.css';

function resolutionTone(ticket: TicketDetailData): 'Pending' | 'Done' | 'Danger' | null {
  if (ticket.awaitsRequesterDecision) return 'Pending';
  if (ticket.status === 'not_confirmed') return 'Danger';
  if (ticket.status === 'rejected') return 'Danger';
  if (ticket.status === 'closed' && ticket.resolutionText) return 'Done';
  return null;
}

function isRejection(ticket: TicketDetailData): boolean {
  return ticket.status === 'rejected';
}

const ResolutionCalloutBase: React.FC<{
  ticket: TicketDetailData;
  showRating?: boolean;
}> = ({ ticket, showRating = false }) => {
  const tone = resolutionTone(ticket);
  if (!tone) return showRating ? <SatisfactionCallout ticket={ticket} /> : null;
  const r = ticket.satisfactionRating;
  const rating =
    showRating && tone !== 'Pending' && r != null && r >= 1 && r <= 5 ? r : null;
  const rejected = isRejection(ticket);
  const text = rejected
    ? ticket.rejectionText || null
    :
      ticket.resolutionText || 'Работы выполнены, ждём проверки.';
  const comment = tone === 'Pending' ? null : (ticket.satisfactionComment ?? null);
  const showVoice = rating != null || (tone === 'Danger' && !rejected && comment);
  return (
    <div className={cn(styles.resolutionCallout, styles[`resolution${tone}`])} role="status">
      { }
      <div className={styles.resolutionHead}>
        <span className={styles.resolutionIcon} aria-hidden>
          {tone === 'Danger' ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
        </span>
        <p className={styles.resolutionTitle}>{rejected ? 'Отклонено' : 'Решение'}</p>
        {tone === 'Pending' && (
          <span className={styles.resolutionState}>Ждёт подтверждения заявителя</span>
        )}
        {tone === 'Danger' && !rejected && (
          <span className={styles.resolutionState}>Заявитель не подтвердил</span>
        )}
      </div>
      <p className={cn(styles.resolutionBody, !text && styles.resolutionBodyEmpty)}>
        {text ?? 'Без указания причины.'}
      </p>
      {showVoice && <SatisfactionLine rating={rating} comment={comment} />}
    </div>
  );
};

export const ResolutionCallout = memo(ResolutionCalloutBase);
